import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../general-components/Button';
import {
    getUploadUrl,
    uploadFile,
    deleteFile,
    renameFile,
    getDepartmentFiles,
    getAllManufacturerFiles,
    getUserFiles,
    getNordinsightFiles,
} from '../../services/api/fileService';
import FileModal from '../video-overview/FileModal';
import FileLink from './FileLink';
import { getFileMimeType, useValidateFile } from '../../utils/files';
import Alert from '../ui-components/Alert';
import ConfirmActionButton from '../general/confirmation/ConfirmActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faChevronDown,
    faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { MultiCombobox } from '../ui/multi-combobox';
import { DataTable, DataTableColumn } from '../ui/data-table';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import PERMISSION_KEYS from '../../constants/permission-keys';

const FileActions = ({
    file,
    onRename,
    onDelete,
}: {
    file: UserFile;
    onRename: (fileId: string, newName: string) => void;
    onDelete: (fileId: string) => void;
}) => {
    const { t } = useTranslation();
    const [newFilename, setNewFilename] = useState(file.filename);

    const handleRename = () => {
        onRename(file._id, newFilename);
    };

    return (
        <div className="flex flex-wrap gap-2">
            <ConfirmActionButton
                buttonText={t('filesOverview.actions.rename.button')}
                buttonType={ButtonType.secondary}
                modalInfo={{
                    title: t('filesOverview.actions.rename.modal.title'),
                    message: t('filesOverview.actions.rename.modal.message', {
                        filename: file.filename,
                    }),
                    submitButtonText: t(
                        'filesOverview.actions.rename.modal.submitButton'
                    ),
                }}
                onConfirm={handleRename}
                onCancel={() => setNewFilename(file.filename)}
            >
                <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="newFilename">
                        {t('filesOverview.actions.rename.modal.label')}
                    </label>
                    <input
                        type="text"
                        id="newFilename"
                        value={newFilename}
                        onChange={(e) => setNewFilename(e.target.value)}
                        style={{
                            color: 'black',
                            width: '100%',
                            padding: '8px',
                            marginTop: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                        }}
                    />
                </div>
            </ConfirmActionButton>

            <ConfirmActionButton
                buttonText={<FontAwesomeIcon icon={faTrash} />}
                buttonType={ButtonType.cancel}
                modalInfo={{
                    title: t('filesOverview.actions.delete.modal.title'),
                    submitButtonText: t(
                        'filesOverview.actions.delete.modal.submitButton'
                    ),
                    submitButtonDanger: true,
                }}
                onConfirm={() => onDelete(file._id)}
            >
                {t('filesOverview.actions.delete.modal.message', {
                    filename: file.filename,
                })}
            </ConfirmActionButton>
        </div>
    );
};

export interface UserFile {
    _id: string;
    title?: string;
    filename: string;
    uploadedBy: string;
    createdAt: string;
    ownerType: string;
    documentType: string;
    mriMachines?: { _id: string; title: string }[];
    internalProtocols?: { _id: string; title: string }[];
    implantNotes?: { _id: string; title: string }[];
    url: string;
    expiry?: number;
    manufacturer?: string;
    mimeType?: string;
}

const AttachmentsToggle = ({ file }: { file: UserFile }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const hasAttachments =
        (file.mriMachines && file.mriMachines.length > 0) ||
        (file.internalProtocols && file.internalProtocols.length > 0) ||
        (file.implantNotes && file.implantNotes.length > 0);

    if (!hasAttachments) {
        return (
            <span className="text-muted-foreground/50">
                {t('filesOverview.attachments.none')}
            </span>
        );
    }

    return (
        <div>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                }}
            >
                {isExpanded
                    ? t('filesOverview.attachments.hide')
                    : t('filesOverview.attachments.show')}
                <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                />
            </button>

            {isExpanded && (
                <div style={{ marginTop: '8px' }}>
                    {file.mriMachines?.length > 0 && (
                        <>
                            <p>{t('filesOverview.attachments.mriMachines')}</p>
                            {file.mriMachines?.map((mriMachine) => (
                                <a href={`/room-overview`} key={mriMachine._id}>
                                    - {mriMachine.title}
                                </a>
                            ))}
                        </>
                    )}
                    {file.internalProtocols?.length > 0 && (
                        <>
                            <p>
                                {t(
                                    'filesOverview.attachments.internalProtocols'
                                )}
                            </p>
                            {file.internalProtocols?.map((internalProtocol) => (
                                <a
                                    href={`/protocols/${internalProtocol._id}`}
                                    key={internalProtocol._id}
                                >
                                    - {internalProtocol.title}
                                </a>
                            ))}
                        </>
                    )}
                    {file.implantNotes?.length > 0 && (
                        <>
                            <p>{t('filesOverview.attachments.implantNotes')}</p>
                            {file.implantNotes?.map((implantNote) => (
                                <a
                                    href={`/implant/${implantNote._id}`}
                                    key={implantNote._id}
                                >
                                    - {implantNote.title}
                                </a>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const FilesTable = ({
    files,
    title,
    showActions,
    onRename,
    onDelete,
    onShowFile,
}: {
    files: UserFile[];
    title: string;
    showActions: boolean;
    onRename: (fileId: string, newName: string) => void;
    onDelete: (fileId: string) => void;
    onShowFile: (file: UserFile) => void;
}) => {
    const { t } = useTranslation();

    if (files.length === 0) return null;

    const columns: DataTableColumn<UserFile>[] = [
        {
            key: 'filename',
            header: t('filesOverview.table.headers.filename'),
            cell: (file) => <FileLink file={file} onClick={onShowFile} />,
            sortValue: (file) => file.filename,
            className: 'font-medium',
        },
        {
            key: 'uploadedBy',
            header: t('filesOverview.table.headers.uploadedBy'),
            cell: (file) => file.uploadedBy,
            sortValue: (file) => file.uploadedBy,
            className: 'text-muted-foreground',
        },
        {
            key: 'createdAt',
            header: t('filesOverview.table.headers.uploadedAt'),
            cell: (file) => new Date(file.createdAt).toLocaleString(),
            sortValue: (file) => new Date(file.createdAt).getTime(),
            className: 'text-muted-foreground',
        },
        {
            key: 'attachedTo',
            header: t('filesOverview.table.headers.attachedTo'),
            cell: (file) => <AttachmentsToggle file={file} />,
            className: 'text-muted-foreground [overflow-wrap:normal]',
        },
        ...(showActions
            ? [
                  {
                      key: 'actions',
                      header: t('filesOverview.table.headers.actions'),
                      cell: (file: UserFile) => (
                          <FileActions
                              file={file}
                              onRename={onRename}
                              onDelete={onDelete}
                          />
                      ),
                      headClassName: 'w-44',
                      className: 'whitespace-nowrap',
                  },
              ]
            : []),
    ];

    return (
        <div>
            <h2 className="mb-3 text-lg font-semibold">{title}</h2>
            <DataTable
                columns={columns}
                items={files}
                rowKey={(file) => file._id}
            />
        </div>
    );
};

const FilesOverviewPage = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const userContext = useContext(AuthContext);

    const [nordinsightFiles, setNordinsightFiles] = useState<UserFile[]>([]);
    const [files, setFiles] = useState<UserFile[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedManufacturers, setSelectedManufacturers] = useState<
        string[]
    >([]);

    const ownerType = userContext.orRights([RIGHTS_KEYS.manageAllImplants])
        ? 'manufacturer'
        : userContext.hasPermission(PERMISSION_KEYS.manageFiles)
        ? 'department'
        : 'user';

    const canManageFileActions = () =>
        userContext.hasPermission(PERMISSION_KEYS.manageFiles) ||
        userContext.orRights([
            RIGHTS_KEYS.manageAllUsers,
            RIGHTS_KEYS.manageAllImplants,
        ]);

    const [isUploading, setIsUploading] = useState(false);
    const [shownFile, setShownFile] = useState<UserFile | null>(null);

    const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);

    const [errors, addError, clearErrors, removeError, validateFile] =
        useValidateFile({
            document: true,
            image: true,
            video: true,
            other: true,
        });

    const allManufacturers = useMemo(
        () =>
            Array.from(
                new Set(
                    files
                        .map((file) => file.manufacturer)
                        .filter(Boolean) as string[]
                )
            ).sort(),
        [files]
    );

    const matchesSearch = (file: UserFile, term: string) =>
        file.filename?.toLowerCase().includes(term) ||
        file.uploadedBy?.toLowerCase().includes(term) ||
        file.manufacturer?.toLowerCase().includes(term);

    const visibleFiles = useMemo(() => {
        let result = files;

        if (selectedManufacturers.length > 0) {
            result = result.filter(
                (file) =>
                    file.manufacturer &&
                    selectedManufacturers.includes(file.manufacturer)
            );
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter((file) => matchesSearch(file, term));
        }

        return result;
    }, [files, searchTerm, selectedManufacturers]);

    const visibleNordinsightFiles = useMemo(() => {
        if (!searchTerm) return nordinsightFiles;
        const term = searchTerm.toLowerCase();
        return nordinsightFiles.filter((file) => matchesSearch(file, term));
    }, [nordinsightFiles, searchTerm]);

    const fetchFiles = async () => {
        if (userContext.orRights([RIGHTS_KEYS.manageAllUsers])) {
            const files = await getNordinsightFiles();
            setNordinsightFiles(files);
        }
        if (userContext.orRights([RIGHTS_KEYS.manageAllImplants])) {
            const files = await getAllManufacturerFiles();
            setFiles(files);
        } else if (userContext.orRights([RIGHTS_KEYS.viewDepartmentFiles])) {
            const files = await getDepartmentFiles();
            setFiles(files);
        } else {
            const files = await getUserFiles();
            setFiles(files);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const getUrlAndUpload = async (ownerType: string, f: File) => {
        if (validateFile(f)) {
            const { url, file } = await getUploadUrl({
                ownerType,
                filename: f.name,
                fileType: getFileMimeType(f),
            });

            const success = await uploadFile(url, f);
            if (success === 'OK') {
                setUploadedFiles((uf) => [...uf, f.name]);
            } else {
                deleteFile(file._id);
                addError({
                    fileName: f.name,
                    errors: [t('filesOverview.alerts.uploadError') + success],
                });
            }
        }
    };

    const handleFileChange = (ownerType: string) => {
        return async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files && event.target.files[0]) {
                setIsUploading(true);
                const files = event.target.files;

                clearErrors();

                await Promise.all(
                    [...files].map((f: File) => getUrlAndUpload(ownerType, f))
                );

                setIsUploading(false);
                fetchFiles();
            }
        };
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleDeleteFile = async (fileId: string) => {
        await deleteFile(fileId);
        fetchFiles();
    };

    const handleRenameFile = async (fileId: string, newName: string) => {
        await renameFile(fileId, newName);
        fetchFiles();
    };

    const showActions = canManageFileActions();

    const renderFileTable = (tableFiles: UserFile[], title: string) => (
        <FilesTable
            files={tableFiles}
            title={title}
            showActions={showActions}
            onRename={handleRenameFile}
            onDelete={handleDeleteFile}
            onShowFile={setShownFile}
        />
    );

    const hasAnyFiles = files.length > 0 || nordinsightFiles.length > 0;
    const hasVisibleFiles =
        visibleFiles.length > 0 || visibleNordinsightFiles.length > 0;

    return (
        <div className="container mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8">
            {shownFile && (
                <FileModal
                    file={{ ...shownFile, title: shownFile.filename }}
                    close={() => setShownFile(null)}
                />
            )}
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-2.5">
                    <h1 className="text-2xl font-bold">
                        {t('filesOverview.departmentFiles.title')}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {t('filesOverview.departmentFiles.supportedTypes')}
                    </p>
                    <ul className="list-disc pl-5 text-sm text-muted-foreground">
                        <li>
                            {t(
                                'filesOverview.departmentFiles.fileTypes.videos'
                            )}
                        </li>
                        <li>
                            {t(
                                'filesOverview.departmentFiles.fileTypes.images'
                            )}
                        </li>
                        <li>
                            {t(
                                'filesOverview.departmentFiles.fileTypes.documents'
                            )}
                        </li>
                    </ul>
                </div>
                {errors.length > 0 && (
                    <div>
                        {errors.map((error) => (
                            <Alert
                                key={error.fileName}
                                title={error.fileName}
                                variant="destructive"
                                onClose={() => removeError(error.fileName)}
                            >
                                {error.errors.map((error) => (
                                    <p key={error}>- {error}</p>
                                ))}
                            </Alert>
                        ))}
                    </div>
                )}
                {uploadedFiles.length > 0 && (
                    <div>
                        {uploadedFiles.map((file) => (
                            <Alert
                                key={file}
                                title={file}
                                variant="success"
                                timed
                                onClose={() =>
                                    setUploadedFiles(
                                        uploadedFiles.filter((f) => f !== file)
                                    )
                                }
                            >
                                <p>{t('filesOverview.alerts.uploadSuccess')}</p>
                            </Alert>
                        ))}
                    </div>
                )}
                {ownerType === 'department' && (
                    <div>
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            onChange={handleFileChange(ownerType)}
                            style={{ display: 'none' }}
                            accept="video/mp4, image/jpeg, image/png, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .doc, .docx, .pptx, .xlsx"
                        />
                        <Button
                            loading={isUploading}
                            type={ButtonType.primary}
                            onClick={handleUploadClick}
                        >
                            {isUploading
                                ? t('filesOverview.departmentFiles.uploading')
                                : t(
                                      'filesOverview.departmentFiles.uploadButton'
                                  )}
                        </Button>
                    </div>
                )}
            </div>

            {hasAnyFiles && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <div className="relative w-full sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t(
                                'filesOverview.filters.searchPlaceholder'
                            )}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    {userContext.orRights([RIGHTS_KEYS.manageAllImplants]) && (
                        <MultiCombobox
                            options={allManufacturers}
                            value={selectedManufacturers}
                            onChange={setSelectedManufacturers}
                            placeholder={t(
                                'filesOverview.filters.manufacturersPlaceholder'
                            )}
                            searchPlaceholder={t(
                                'filesOverview.filters.manufacturersSearchPlaceholder'
                            )}
                            emptyText={t(
                                'filesOverview.filters.noManufacturers'
                            )}
                            className="w-full sm:w-56"
                            aria-label="Filter by manufacturer"
                        />
                    )}
                </div>
            )}

            {hasAnyFiles && !hasVisibleFiles && (
                <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                    <Search className="mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="max-w-md text-sm text-muted-foreground">
                        {t('filesOverview.filters.noResults')}
                    </p>
                </div>
            )}

            {visibleFiles.length > 0 && (
                <>
                    {renderFileTable(
                        visibleFiles.filter(
                            (file) => file.documentType === 'document'
                        ),
                        t('filesOverview.fileTypes.documents')
                    )}
                    {renderFileTable(
                        visibleFiles.filter(
                            (file) => file.documentType === 'image'
                        ),
                        t('filesOverview.fileTypes.images')
                    )}
                    {renderFileTable(
                        visibleFiles.filter(
                            (file) => file.documentType === 'video'
                        ),
                        t('filesOverview.fileTypes.videos')
                    )}
                    {renderFileTable(
                        visibleFiles.filter(
                            (file) =>
                                file.documentType !== 'document' &&
                                file.documentType !== 'image' &&
                                file.documentType !== 'video'
                        ),
                        t('filesOverview.fileTypes.other')
                    )}
                </>
            )}
            {visibleNordinsightFiles.length > 0 &&
                renderFileTable(visibleNordinsightFiles, 'Nordinsight Files')}
        </div>
    );
};

export default FilesOverviewPage;
